// https://ulap.biz/app/trans/ROGmJ
// PCASE Number 2025-07-0900

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Divider,
  Grid,
  Chip,
  Avatar,
  MenuItem,
  IconButton,
  makeStyles,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  CardHeader,
  Select,
  InputBase,
  withStyles,
  useTheme
} from '@material-ui/core';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Storage,
  Person,
  Group
} from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { useRequest, useNotif } from 'src/hooks';
import SelectWithSearchV2 from 'src/components/SelectWithSearchV2';
import { makeStyles as muiMakeStyles } from '@material-ui/core/styles';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ChipContainer from 'src/views/trans/TransView/ChipContainer';

const useStyles = muiMakeStyles(theme => ({
  tabPanel: {
    marginTop: theme.spacing(2)
  },
  chip: {
    margin: theme.spacing(0.5),
    fontFamily: "'Poppins', sans-serif"
  },
  dialogContent: {
    paddingTop: '8px !important',
    paddingBottom: '24px !important'
  },
  assignedToRoot: {
    background: '#fff',
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    minHeight: 80
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1)
  },
  icon: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1)
  },
  chipsWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center',
    minHeight: 40
  },
  noApprover: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    fontSize: '1em',
    fontFamily: "'Poppins', sans-serif"
  },
  typography: {
    fontFamily: "'Poppins', sans-serif"
  },
  tableCell: {
    fontFamily: "'Poppins', sans-serif"
  },
  button: {
    fontFamily: "'Poppins', sans-serif"
  }
}));

const UserMenuItem = ({ value, description, user, ...props }) => (
  <MenuItem {...props}>
    <Box display="flex" alignItems="center" gridGap="1em">
      <Avatar src={user?.photoURL} />
      <Typography>{description}</Typography>
    </Box>
  </MenuItem>
);

const UserSelect = ({ label, value, onChange, options }) => (
  <SelectWithSearchV2
    label={label}
    value={value}
    onChange={e => onChange(e.target.value)}
    options={options}
    multiple
    CustomMenuItem={UserMenuItem}
    searchKeys={['description']}
  />
);

const EntitySelect = ({ label, value, onChange, options, ...otherProps }) => (
  <SelectWithSearchV2
    label={label}
    value={value ? [String(value)] : []}
    onChange={e =>
      onChange(
        Array.isArray(e.target.value) ? e.target.value[0] || '' : e.target.value
      )
    }
    options={options}
    multiple={false}
    searchKeys={['label', 'description']}
    {...otherProps}
  />
);

function groupSectionsByTransaction(kvs) {
  const grouped = {};
  Object.entries(kvs).forEach(([key, config]) => {
    if (config && Array.isArray(config.ixJCd)) {
      config.ixJCd.forEach(ixJCd => {
        if (!grouped[ixJCd]) grouped[ixJCd] = [];
        grouped[ixJCd].push({
          key,
          label: config.id1?.label || '',
          type: key.includes('approver') ? 'Approver' : 'Reviewer',
          config
        });
      });
    }
  });
  return grouped;
}

const OrangeInput = withStyles(theme => ({
  input: {
    borderRadius: 6,
    position: 'relative',
    backgroundColor: '#fff',
    border: '2px solid #ff9800',
    fontSize: 18,
    padding: '10px 26px 10px 12px',
    fontWeight: 700,
    color: '#ff9800',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:focus': {
      borderColor: '#ff9800',
      boxShadow: '0 0 0 2px rgba(255,152,0,0.2)'
    }
  }
}))(InputBase);

const UserChoices = ({ onFetchingChange = () => {} }) => {
  const classes = useStyles();
  const theme = useTheme();
  const req = useRequest();
  const notif = useNotif();
  const { kvs = {} } = useSelector(state => state.bizMeta);
  const { users: bizUsers = [] } = useSelector(state => state.bizMeta);

  // Extract transactions dynamically from kvs (per transaction)
  // const transactionMap = {};
  // Object.values(kvs).forEach(config => {
  //   if (
  //     config &&
  //     Array.isArray(config.ixJCd) &&
  //     config.id1?.label
  //   ) {
  //     config.ixJCd.forEach(ixJCd => {
  //       transactionMap[ixJCd] = config.id1.label;
  //     });
  //   }
  // });
  // const TRANSACTIONS = [];
  // const seen = new Set();
  // Object.values(kvs).forEach(config => {
  //   if (
  //     config &&
  //     config.ui_component === 'user_choices' &&
  //     Array.isArray(config.ixJCd)
  //   ) {
  //     config.ixJCd.forEach(ixJCd => {
  //       if (!seen.has(ixJCd)) {
  //         TRANSACTIONS.push({
  //           ixJCd,
  //           name: config.transaction_name || `Transaction ${ixJCd}`
  //         });
  //         seen.add(ixJCd);
  //       }
  //     });
  //   }
  // });

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [mappings, setMappings] = useState({});
  const [entityOptions, setEntityOptions] = useState({});
  const [modal, setModal] = useState({
    open: false,
    type: '',
    item: null,
    isEditing: false
  });
  // const [selectedIxJCd, setSelectedIxJCd] = useState(TRANSACTIONS[0].ixJCd);

  const isMounted = useRef(true);

  const getDynamicApproverConfig = useCallback(() => {
    const dynamicConfig = {};
    const dynamicSectionLabels = {};
    const dynamicEntityLabels = {};
    const dynamicSectionTypes = {};
    const dynamicSectionTitles = {};
    const dynamicAddLabels = {};
    const dynamicGroupKeys = {};
    const dynamicGroupTitleOverrides = {};

    Object.entries(kvs).forEach(([key, config]) => {
      if (
        config?.ui_component === 'user_choices' &&
        config?.id1?.label &&
        typeof config?.id1?.ixSubType === 'number'
      ) {
        dynamicConfig[key] = key;
        dynamicSectionLabels[key] = config.id1.label;
        dynamicEntityLabels[key] = config.id1.label;

        const rawType = String(
          config.role ||
            config.section_type ||
            config.type_label ||
            config.assignee_type ||
            ''
        ).toLowerCase();
        const resolvedType = rawType.startsWith('approv')
          ? 'Approver'
          : rawType.startsWith('review')
          ? 'Reviewer'
          : 'Reviewer';
        dynamicSectionTypes[key] = resolvedType;

        // Per-section overrides
        const computedDefaultTitle = `${config.id1.label} ${resolvedType}`;
        dynamicSectionTitles[key] =
          config.section_title || computedDefaultTitle;
        dynamicAddLabels[key] =
          config.add_button_label || `Add ${resolvedType}`;

        // Grouping
        const groupKey =
          config.page_title_group || config.group_id || config.group || '';
        const groupTitleOverride =
          config.group_title || config.page_title_group_title || '';
        dynamicGroupKeys[key] = groupKey ? String(groupKey) : '';
        dynamicGroupTitleOverrides[key] = groupTitleOverride
          ? String(groupTitleOverride)
          : '';
      }
    });

    return {
      dynamicConfig,
      dynamicSectionLabels,
      dynamicEntityLabels,
      dynamicSectionTypes,
      dynamicSectionTitles,
      dynamicAddLabels,
      dynamicGroupKeys,
      dynamicGroupTitleOverrides
    };
  }, [kvs]);

  const {
    dynamicConfig,
    dynamicSectionLabels,
    dynamicEntityLabels,
    dynamicSectionTypes,
    dynamicSectionTitles,
    dynamicAddLabels,
    dynamicGroupKeys,
    dynamicGroupTitleOverrides
  } = getDynamicApproverConfig();
  const entityTypes = Object.keys(dynamicConfig);

  // Determine page title from settings (supports grouping or hiding)
  const { pageTitle, pageTitleMode } = useMemo(() => {
    const userChoices = Object.values(kvs || {}).filter(
      cfg => cfg && cfg.ui_component === 'user_choices'
    );
    const modeFromCfg = userChoices.find(
      c =>
        typeof c.page_title_mode === 'string' ||
        typeof c.group_title === 'boolean'
    );
    const pageTitleMode =
      modeFromCfg?.page_title_mode ||
      (modeFromCfg?.group_title ? 'group' : undefined) ||
      'group';
    const globalOverride = userChoices.find(
      c => typeof c.page_title_global === 'string'
    )?.page_title_global;
    const titlesSet = new Set(
      userChoices.map(c => c.title || c.page_title).filter(Boolean)
    );
    let pageTitle = '';
    if (pageTitleMode === 'group') {
      if (globalOverride) {
        pageTitle = globalOverride;
      } else if (titlesSet.size === 1) {
        pageTitle = Array.from(titlesSet)[0] || '';
      } else {
        pageTitle = '';
      }
    }
    return { pageTitle, pageTitleMode };
  }, [kvs]);

  const fetchAllData = useCallback(async () => {
    setFetching(true);
    setLoading(true);
    try {
      const mappingKeys = Object.values(dynamicConfig);
      const mappingRes = await req.post('/setup/biz/kvs/items', {
        keys: mappingKeys
      });
      const newMappings = {};
      entityTypes.forEach(type => {
        newMappings[type] = [];
      });
      (mappingRes.data || []).forEach(item => {
        const type = Object.keys(dynamicConfig).find(
          t => dynamicConfig[t] === item.key.trim()
        );
        let assignees = [];
        if (Array.isArray(item.value)) {
          assignees = item.value;
        } else if (typeof item.value === 'string') {
          try {
            const parsed = JSON.parse(item.value);
            assignees = Array.isArray(parsed) ? parsed : [item.value];
          } catch {
            assignees = [item.value];
          }
        }
        if (type && item.id1 && assignees.length > 0) {
          newMappings[type].push({
            entity: item.id1,
            assignees
          });
        }
      });
      if (isMounted.current) setMappings(newMappings);

      // logic for sub-entity options
      const newEntityOptions = { ...entityOptions };
      for (const type of entityTypes) {
        if (!newEntityOptions[type] || newEntityOptions[type].length === 0) {
          const kvsKey = dynamicConfig[type];
          const kvsSetting = kvs[kvsKey];
          if (
            kvsSetting &&
            kvsSetting.id1 &&
            typeof kvsSetting.id1.ixSubType === 'number' &&
            kvsSetting.id1.label
          ) {
            const subRes = await req.post('/lib/sub', {
              ixSubType: kvsSetting.id1.ixSubType
            });
            newEntityOptions[type] = (subRes.data?.sub_list || []).map(i => ({
              value: String(i.id || i.sub_id),
              label: i.name || i.sub_title,
              description: i.name || i.sub_title
            }));
          } else {
            newEntityOptions[type] = [];
          }
        }
      }
      if (isMounted.current) setEntityOptions(newEntityOptions);
    } catch (error) {
      notif.error('Failed to load settings data.');
    } finally {
      if (isMounted.current) setLoading(false);
      if (isMounted.current) setFetching(false);
    }
  }, [dynamicConfig, kvs, entityOptions]);

  useEffect(() => {
    isMounted.current = true;
    fetchAllData();
    return () => {
      isMounted.current = false;
    };
  }, [Object.keys(kvs).join(',')]);

  const handleOpenModal = (type, item = null) => {
    setModal({
      open: true,
      type,
      item: item || { entity: '', assignees: [] },
      isEditing: !!item
    });
  };

  const handleCloseModal = () => {
    setModal({ open: false, type: '', item: null, isEditing: false });
  };

  const handleModalChange = (field, value) => {
    setModal(prev => ({ ...prev, item: { ...prev.item, [field]: value } }));
  };

  const handleSave = async () => {
    const { type, item, isEditing } = modal;
    const kvsKey = dynamicConfig[type];

    if (!item.entity || !item.assignees || item.assignees.length === 0) {
      return notif.error('Please fill all fields.');
    }

    let newEntry = { ...item };
    let currentEntries = mappings[type] || [];

    let updatedEntries = currentEntries.filter(
      e => !(String(e.entity) === String(item.entity))
    );

    updatedEntries.push(newEntry);

    const endpoint = '/setup/biz/kvs/save';
    const payload = {
      [kvsKey]: {
        id1: item.entity,
        value: item.assignees
      }
    };

    setLoading(true);
    try {
      await req.post(endpoint, payload);
      notif.success('Approver saved successfully.');
      handleCloseModal();
      await fetchAllData();
    } catch (error) {
      notif.error('Failed to save approver.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type, entityId) => {
    const kvsKey = dynamicConfig[type];

    setLoading(true);
    try {
      await req.post('/setup/biz/kvs/delete', {
        [kvsKey]: { id1: entityId }
      });

      if (!isMounted.current) return;
      notif.success('Approver deleted successfully.');
      await fetchAllData();
    } catch (error) {
      if (!isMounted.current) return;
      notif.error('Failed to delete approver.');
    } finally {
      if (!isMounted.current) return;
      setLoading(false);
    }
  };

  const userOptions = bizUsers.map(u => ({
    value: u.username,
    description: u.name || u.username,
    user: u
  }));

  const renderEntityName = (type, entityId) => {
    const options = entityOptions[type] || [];
    const option = options.find(opt => String(opt.value) === String(entityId));
    return option ? option.label : 'Unknown';
  };

  const renderEntityId = entityId => entityId;

  const renderAssigneeChips = assignees => {
    return (
      <Box display="flex" flexDirection="column" alignItems="flex-start">
        {assignees.map((username, i) => (
          <Chip
            key={i}
            avatar={
              <Avatar src={`/api/images/user/${username}/avatar.jpg`}>
                {(username?.[0] || '').toUpperCase()}
              </Avatar>
            }
            label={username}
            color="primary"
            size="small"
            style={{
              marginBottom: 6,
              fontWeight: 400,
              borderRadius: 12,
              fontFamily: "'Poppins', sans-serif"
            }}
          />
        ))}
      </Box>
    );
  };

  const getSectionsForTransaction = (kvs, ixJCd) =>
    Object.entries(kvs)
      .filter(
        ([key, config]) =>
          config && Array.isArray(config.ixJCd) && config.ixJCd.includes(ixJCd)
      )
      .map(([key, config]) => ({
        key,
        label: config.id1?.label || '',
        type: key.includes('approver') ? 'Approver' : 'Reviewer'
      }));

  const filteredSections = Object.entries(kvs || {})
    .filter(
      ([key, config]) =>
        config && config.ui_component === 'user_choices' && config.id1?.label
    )
    .map(([key, config]) => ({
      key,
      label: config.id1?.label || '',
      type: dynamicSectionTypes[key] || 'Reviewer'
    }));

  // Grouping logic: if any section has a group key, render by group
  const { hasGrouping, groups } = useMemo(() => {
    const items = filteredSections;
    const anyGroup = items.some(
      s => (dynamicGroupKeys[s.key] || '').length > 0
    );
    if (!anyGroup) return { hasGrouping: false, groups: {} };

    const groups = {};
    items.forEach(s => {
      const gk = dynamicGroupKeys[s.key] || '__ungrouped__';
      if (!groups[gk]) groups[gk] = { title: '', items: [] };
      groups[gk].items.push(s);
    });

    // group titles
    Object.entries(groups).forEach(([gk, group]) => {
      const overrides = group.items
        .map(s => dynamicGroupTitleOverrides[s.key])
        .filter(Boolean);
      if (overrides.length > 0) {
        groups[gk].title = overrides[0];
        return;
      }
      const titlesSet = new Set(
        group.items
          .map(s => kvs[s.key]?.title || kvs[s.key]?.page_title || '')
          .filter(Boolean)
      );
      groups[gk].title = titlesSet.size === 1 ? Array.from(titlesSet)[0] : '';
    });

    return { hasGrouping: true, groups };
  }, [filteredSections, dynamicGroupKeys, dynamicGroupTitleOverrides, kvs]);

  //fix table UI alignment of columns
  return (
    <Box>
      {!hasGrouping && pageTitle ? (
        <Typography variant="h5" gutterBottom>
          {pageTitle}
        </Typography>
      ) : null}
      {fetching ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={240}
        >
          <CircularProgress size={48} thickness={4.5} color="primary" />
          <Typography
            variant="subtitle1"
            color="textSecondary"
            style={{ marginTop: 16, fontFamily: "'Poppins', sans-serif" }}
          >
            Loading data...
          </Typography>
        </Box>
      ) : hasGrouping ? (
        Object.entries(groups).map(([gk, group]) => (
          <Box key={gk} mb={3}>
            {group.title ? (
              <Typography variant="h5" gutterBottom>
                {group.title}
              </Typography>
            ) : null}
            {group.items.map(section => (
              <Paper
                key={section.key}
                elevation={3}
                style={{
                  marginBottom: 32,
                  borderRadius: 8,
                  padding: 24,
                  background: '#fff'
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={2}
                >
                  <Box display="flex" alignItems="center">
                    {section.type === 'Approver' ? (
                      <Person style={{ color: '#222', marginRight: 8 }} />
                    ) : (
                      <Group style={{ color: '#222', marginRight: 8 }} />
                    )}
                    <Typography variant="h6" style={{ fontWeight: 700 }}>
                      {dynamicSectionTitles[section.key] ||
                        `${section.label} ${section.type}`}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    style={{ borderRadius: 6, fontWeight: 600 }}
                    onClick={() => handleOpenModal(section.key)}
                  >
                    {dynamicAddLabels[section.key] || `Add ${section.type}`}
                  </Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          style={{ background: '#fff', fontWeight: 700 }}
                        >
                          {section.label}
                        </TableCell>
                        <TableCell
                          style={{ minWidth: 100, width: 120, fontWeight: 700 }}
                        >
                          ID
                        </TableCell>
                        <TableCell style={{ minWidth: 250, fontWeight: 700 }}>
                          Assignees
                        </TableCell>
                        <TableCell style={{ width: 100, fontWeight: 700 }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(mappings[section.key] || []).map((item, idx) => (
                        <TableRow
                          key={idx}
                          style={{
                            background: idx % 2 === 0 ? '#f7f7f7' : '#fff'
                          }}
                        >
                          <TableCell style={{ minWidth: 180, width: 200 }}>
                            {renderEntityName(section.key, item.entity)}
                          </TableCell>
                          <TableCell style={{ minWidth: 100, width: 120 }}>
                            {renderEntityId(item.entity)}
                          </TableCell>
                          <TableCell style={{ minWidth: 250 }}>
                            {renderAssigneeChips(item.assignees)}
                          </TableCell>
                          <TableCell style={{ width: 100 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenModal(section.key, item)}
                            >
                              <EditIcon
                                style={{ color: theme.palette.primary.main }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDelete(section.key, item.entity)
                              }
                            >
                              <DeleteIcon
                                style={{ color: theme.palette.error.main }}
                              />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ))}
          </Box>
        ))
      ) : (
        filteredSections.map(section => (
          <Paper
            key={section.key}
            elevation={3}
            style={{
              marginBottom: 32,
              borderRadius: 8,
              padding: 24,
              background: '#fff'
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <Box display="flex" alignItems="center">
                {section.type === 'Approver' ? (
                  <Person style={{ color: '#222', marginRight: 8 }} />
                ) : (
                  <Group style={{ color: '#222', marginRight: 8 }} />
                )}
                <Typography variant="h6" style={{ fontWeight: 700 }}>
                  {dynamicSectionTitles[section.key] ||
                    `${section.label} ${section.type}`}
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                style={{ borderRadius: 6, fontWeight: 600 }}
                onClick={() => handleOpenModal(section.key)}
              >
                {dynamicAddLabels[section.key] || `Add ${section.type}`}
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ background: '#fff', fontWeight: 700 }}>
                      {section.label}
                    </TableCell>
                    <TableCell
                      style={{ minWidth: 100, width: 120, fontWeight: 700 }}
                    >
                      ID
                    </TableCell>
                    <TableCell style={{ minWidth: 250, fontWeight: 700 }}>
                      Assignees
                    </TableCell>
                    <TableCell style={{ width: 100, fontWeight: 700 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(mappings[section.key] || []).map((item, idx) => (
                    <TableRow
                      key={idx}
                      style={{ background: idx % 2 === 0 ? '#f7f7f7' : '#fff' }}
                    >
                      <TableCell style={{ minWidth: 180, width: 200 }}>
                        {renderEntityName(section.key, item.entity)}
                      </TableCell>
                      <TableCell style={{ minWidth: 100, width: 120 }}>
                        {renderEntityId(item.entity)}
                      </TableCell>
                      <TableCell style={{ minWidth: 250 }}>
                        {renderAssigneeChips(item.assignees)}
                      </TableCell>
                      <TableCell style={{ width: 100 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal(section.key, item)}
                        >
                          <EditIcon
                            style={{ color: theme.palette.primary.main }}
                          />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(section.key, item.entity)}
                        >
                          <DeleteIcon
                            style={{ color: theme.palette.error.main }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))
      )}
      <Dialog
        open={modal.open}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className={classes.typography}>
          {modal.isEditing ? 'Edit' : 'Add'}{' '}
          {modal.type ? dynamicSectionTypes[modal.type] || 'Reviewer' : ''} for{' '}
          {modal.type ? dynamicEntityLabels[modal.type] : ''}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <EntitySelect
                label={modal.type ? dynamicEntityLabels[modal.type] : ''}
                value={modal.item?.entity}
                onChange={val => handleModalChange('entity', val)}
                options={entityOptions[modal.type] || []}
                disabled={modal.isEditing}
              />
            </Grid>
            <Grid item xs={12}>
              <UserSelect
                label="Assignee(s)"
                value={modal.item?.assignees || []}
                onChange={val => handleModalChange('assignees', val)}
                options={userOptions}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseModal}
            color="secondary"
            disabled={loading}
            className={classes.button}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={loading}
            className={classes.button}
          >
            {loading ? 'Saving...' : modal.isEditing ? 'Save Changes' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserChoices;
